import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, X } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const empty = {
  name: "", slug: "", category: "", short_description: "", description: "",
  ingredients: "", allergens: "",
  image_url: "", gallery_str: "",
  sizes_str: '3"|Individual serving|CHOC-03\n10"|Serves 12-16|CHOC-10',
  featured: false, active: true,
};

function parseSizes(str) {
  return str.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
    const [label, servings = "", sku = ""] = line.split("|").map((x) => x.trim());
    return { label, servings, sku };
  });
}

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...p,
      gallery_str: (p.gallery || []).join("\n"),
      sizes_str: (p.sizes || []).map((s) => `${s.label}|${s.servings || ""}|${s.sku || ""}`).join("\n"),
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      name: form.name,
      slug: form.slug,
      category: form.category,
      short_description: form.short_description,
      description: form.description,
      ingredients: form.ingredients,
      allergens: form.allergens,
      image_url: form.image_url,
      gallery: form.gallery_str.split("\n").map((s) => s.trim()).filter(Boolean),
      sizes: parseSizes(form.sizes_str),
      featured: !!form.featured,
      active: form.active !== false,
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, body);
      else await api.post(`/admin/products`, body);
      toast.success(editing ? "Product updated" : "Product created");
      setShowForm(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Save failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Product deleted");
    load();
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Catalog</div>
          <h1 className="font-display text-4xl text-brand-ink mt-2">Products</h1>
        </div>
        <button onClick={openNew} data-testid="new-product-btn" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand-ink text-white text-sm font-semibold hover:bg-brand-ink transition">
          <Plus className="w-4 h-4" /> New product
        </button>
      </div>

      <div className="mt-8 grid gap-3">
        {items.map((p) => (
          <div key={p.id} data-testid={`product-row-${p.slug}`} className="bg-white border border-brand-line rounded-2xl p-4 flex items-center gap-4">
            <img src={p.image_url} alt="" className="w-16 h-16 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-brand-ink">{p.name}</div>
              <div className="text-xs text-brand-stone">{p.category} · {p.sizes.map((s) => s.label).join(" · ")}</div>
            </div>
            {p.featured && <span className="text-[10px] uppercase tracking-widest text-brand-red font-bold">Featured</span>}
            <button onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`} className="w-9 h-9 grid place-items-center rounded-full border border-brand-line hover:bg-brand-pink/40"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(p)} data-testid={`delete-${p.slug}`} className="w-9 h-9 grid place-items-center rounded-full border border-brand-line hover:bg-red-50 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-brand-ink/40 backdrop-blur-sm grid place-items-center p-4">
          <div className="bg-white rounded-3xl border border-brand-line max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-warm-lg">
            <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-brand-line">
              <h2 className="font-display text-2xl text-brand-ink">{editing ? "Edit product" : "New product"}</h2>
              <button onClick={() => setShowForm(false)} className="w-9 h-9 grid place-items-center rounded-full hover:bg-brand-pink/40"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={submit} className="p-6 space-y-4" data-testid="product-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <F label="Name" required value={form.name} onChange={set("name")} />
                <F label="Slug" required value={form.slug} onChange={set("slug")} />
                <F label="Category" required value={form.category} onChange={set("category")} />
              </div>
              <ImageUploader
                label="Main image"
                value={form.image_url}
                onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                testIdPrefix="product-image"
              />
              <F label="Short description" required value={form.short_description} onChange={set("short_description")} />
              <T label="Description" required value={form.description} onChange={set("description")} rows={4} />
              <T label="Ingredients" value={form.ingredients} onChange={set("ingredients")} rows={2} />
              <T label="Allergens" value={form.allergens} onChange={set("allergens")} rows={2} />
              <T label="Gallery (one URL per line)" value={form.gallery_str} onChange={set("gallery_str")} rows={3} />
              <T label='Sizes (one per line, format: label|servings|sku e.g. 3"|Individual|CHOC-03)' value={form.sizes_str} onChange={set("sizes_str")} rows={3} />

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.featured} onChange={set("featured")} /> Featured</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active !== false} onChange={set("active")} /> Active</label>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" data-testid="save-product" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand-ink text-white text-sm font-semibold hover:bg-brand-ink">Save</button>
                <button type="button" onClick={() => setShowForm(false)} className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-brand-line text-brand-ink text-sm font-semibold">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, required, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-brand-ink">{label} {required && <span className="text-brand-red">*</span>}</label>
      <input type={type} value={value || ""} required={required} onChange={onChange}
        className="mt-1.5 w-full h-11 rounded-xl bg-white border border-brand-line px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red" />
    </div>
  );
}
function T({ label, required, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-brand-ink">{label} {required && <span className="text-brand-red">*</span>}</label>
      <textarea rows={rows} value={value || ""} required={required} onChange={onChange}
        className="mt-1.5 w-full rounded-xl bg-white border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red resize-none font-mono" />
    </div>
  );
}
